export const TOTAL_AAL_LABEL = 'Total AAL'
export const TOTAL_LOSS_LABEL = 'Total Loss'
export const DAMAGE_AAL_LABEL = 'Damage AAL'
export const REVENUE_AAL_LABEL = 'Revenue AAL'
export const BI_DAYS_LABEL = 'BI Days'
export const FINANCIAL_BREAKDOWN_TITLE = 'Financial Breakdown'
export const EVENT_IMPACTS_TITLE = 'Event Impacts'

export type AnnualLossDisplay = {
  title: string
  value: number | null
}

function titleCase(value: string): string {
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function parseMetricNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export function parseMetricString(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length ? trimmed : null
  }
  if (value && typeof value === 'object') {
    const direct = parseMetricString((value as any).value)
    if (direct) return direct
    const yValue = parseMetricString((value as any).y)
    if (yValue) return yValue
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = parseMetricString(item)
      if (nested) return nested
    }
  }
  return null
}

export function parseMetricPointY(value: unknown): number | null {
  if (Array.isArray(value)) {
    const first = value[0]
    if (first && typeof first === 'object' && 'y' in first) {
      return parseMetricNumber((first as any).y)
    }
    return parseMetricNumber(first)
  }

  if (value && typeof value === 'object' && 'y' in (value as any)) {
    return parseMetricNumber((value as any).y)
  }

  return parseMetricNumber(value)
}

export function sumMetricNode(value: unknown): number | null {
  if (Array.isArray(value)) {
    let total = 0
    let found = false

    value.forEach(item => {
      if (item && typeof item === 'object' && 'x' in item && 'y' in item) {
        const y = parseMetricNumber((item as any).y)
        if (y != null) {
          total += y
          found = true
        }
        return
      }

      const nested = sumMetricNode(item)
      if (nested != null) {
        total += nested
        found = true
      }
    })

    return found ? total : null
  }

  if (value && typeof value === 'object') {
    if ('x' in (value as any) && 'y' in (value as any)) {
      return parseMetricNumber((value as any).y)
    }

    let total = 0
    let found = false

    Object.values(value as Record<string, unknown>).forEach(item => {
      const nested = sumMetricNode(item)
      if (nested != null) {
        total += nested
        found = true
      }
    })

    return found ? total : null
  }

  return parseMetricNumber(value)
}

export function collectSeriesByYear(value: unknown): Record<number, number> {
  const out: Record<number, number> = {}

  const visit = (node: unknown) => {
    if (Array.isArray(node)) {
      node.forEach(item => {
        if (item && typeof item === 'object' && 'x' in item && 'y' in item) {
          const year = parseMetricNumber((item as any).x)
          const valueY = parseMetricNumber((item as any).y)
          if (year != null && valueY != null) {
            const roundedYear = Math.round(year)
            out[roundedYear] = (out[roundedYear] ?? 0) + valueY
          }
          return
        }
        visit(item)
      })
      return
    }

    if (node && typeof node === 'object') {
      if ('x' in (node as any) && 'y' in (node as any)) {
        const year = parseMetricNumber((node as any).x)
        const valueY = parseMetricNumber((node as any).y)
        if (year != null && valueY != null) {
          const roundedYear = Math.round(year)
          out[roundedYear] = (out[roundedYear] ?? 0) + valueY
        }
        return
      }

      Object.values(node as Record<string, unknown>).forEach(visit)
    }
  }

  visit(value)

  return out
}

export function collectEventMetricByReturnPeriod(
  value: unknown,
  aggregation: 'sum' | 'mean' = 'sum',
): Record<string, Record<number, number>> {
  const samples: Record<string, Record<number, number[]>> = {}

  const collectSeriesSamples = (node: unknown, returnPeriod: string) => {
    const visitSeries = (seriesNode: unknown) => {
      if (Array.isArray(seriesNode)) {
        seriesNode.forEach(item => {
          if (item && typeof item === 'object' && 'x' in item && 'y' in item) {
            const year = parseMetricNumber((item as any).x)
            const valueY = parseMetricNumber((item as any).y)
            if (year != null && valueY != null) {
              const roundedYear = Math.round(year)
              if (!samples[returnPeriod]) samples[returnPeriod] = {}
              if (!samples[returnPeriod][roundedYear]) samples[returnPeriod][roundedYear] = []
              samples[returnPeriod][roundedYear].push(valueY)
            }
            return
          }
          visitSeries(item)
        })
        return
      }

      if (seriesNode && typeof seriesNode === 'object') {
        if ('x' in (seriesNode as any) && 'y' in (seriesNode as any)) {
          const year = parseMetricNumber((seriesNode as any).x)
          const valueY = parseMetricNumber((seriesNode as any).y)
          if (year != null && valueY != null) {
            const roundedYear = Math.round(year)
            if (!samples[returnPeriod]) samples[returnPeriod] = {}
            if (!samples[returnPeriod][roundedYear]) samples[returnPeriod][roundedYear] = []
            samples[returnPeriod][roundedYear].push(valueY)
          }
          return
        }

        Object.values(seriesNode as Record<string, unknown>).forEach(visitSeries)
      }
    }

    visitSeries(node)
  }

  const visit = (node: unknown) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return

    Object.entries(node as Record<string, unknown>).forEach(([key, child]) => {
      if (/^\d+(\.\d+)?$/.test(key)) {
        collectSeriesSamples(child, key)
        return
      }

      visit(child)
    })
  }

  visit(value)

  const out: Record<string, Record<number, number>> = {}
  Object.entries(samples).forEach(([returnPeriod, byYear]) => {
    out[returnPeriod] = {}
    Object.entries(byYear).forEach(([year, values]) => {
      out[returnPeriod][Number(year)] =
        aggregation === 'mean'
          ? values.reduce((sum, item) => sum + item, 0) / values.length
          : values.reduce((sum, item) => sum + item, 0)
    })
  })

  return out
}

export function sumNumbers(values: Array<number | null | undefined>): number | null {
  let total = 0
  let found = false

  values.forEach(value => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      total += value
      found = true
    }
  })

  return found ? total : null
}

export function meanNumbers(values: Array<number | null | undefined>): number | null {
  const finite = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  if (!finite.length) return null
  return finite.reduce((sum, value) => sum + value, 0) / finite.length
}

export function buildAnnualLossDisplay(
  damageAal: number | null | undefined,
  revenueAal: number | null | undefined,
): AnnualLossDisplay {
  const hasDamage = typeof damageAal === 'number' && Number.isFinite(damageAal)
  const hasRevenue = typeof revenueAal === 'number' && Number.isFinite(revenueAal)

  if (hasDamage && hasRevenue) {
    return { title: TOTAL_AAL_LABEL, value: (damageAal as number) + (revenueAal as number) }
  }

  if (hasDamage) {
    return { title: DAMAGE_AAL_LABEL, value: damageAal as number }
  }

  if (hasRevenue) {
    return { title: REVENUE_AAL_LABEL, value: revenueAal as number }
  }

  return { title: TOTAL_AAL_LABEL, value: null }
}

export function formatDamageTargetLabel(target: string): string {
  switch (target) {
    case 'building':
    case 'structure':
      return 'Structure'
    case 'contents':
      return 'Contents'
    default:
      return titleCase(target)
  }
}

export function formatFloodSourceLabel(source: string): string {
  switch (source) {
    case 'river':
      return 'River'
    case 'surface':
      return 'Surface'
    case 'coastal':
      return 'Coastal'
    default:
      return titleCase(source)
  }
}

export function formatReturnPeriodLabel(returnPeriod: string): string {
  return `${returnPeriod}-year event`
}

export function formatPlainNumber(value: number | null | undefined, digits = 1): string {
  if (value == null || Number.isNaN(value)) return '—'
  return value.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  })
}

export function formatCompactNumber(value: number | null | undefined, digits = 1): string {
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: digits,
  }).format(value)
}

export function formatCurrencyValue(
  value: number | null | undefined,
  currencySymbol: string,
  digits = 0,
  compact = false,
): string {
  if (value == null || Number.isNaN(value)) return '—'
  const formatted = compact ? formatCompactNumber(value, digits) : formatPlainNumber(value, digits)
  return `${currencySymbol}${formatted}`
}

export function formatPercentValue(value: number | null | undefined, digits = 1): string {
  if (value == null || Number.isNaN(value)) return '—'
  return `${(value * 100).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  })}%`
}
